// src/contexts/InventoryContext.tsx
// Stato del magazzino condiviso fra la pagina Magazzino e la vendita rapida in agenda.
import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import {
    fetchProducts,
    fetchMovements,
    createProduct as createProductService,
    updateProduct as updateProductService,
    archiveProduct as archiveProductService,
    deleteProduct as deleteProductService,
    registerMovement as registerMovementService,
    registerSale as registerSaleService,
    InventoryProduct,
    InventoryMovement,
    MovementType,
    SaleLine,
    CreateProductInput,
} from '@/services/inventory/inventoryService';

interface InventoryContextValue {
    products: InventoryProduct[];
    movements: InventoryMovement[];
    loading: boolean;
    refresh: () => Promise<void>;
    findByBarcode: (barcode: string) => InventoryProduct | undefined;
    getProductById: (id: number) => InventoryProduct | undefined;
    hasMovements: (productId: number) => boolean;
    addProduct: (
        input: Omit<CreateProductInput, 'shop_id'>
    ) => Promise<{ success: boolean; error?: string; data?: InventoryProduct }>;
    editProduct: (
        id: number,
        patch: Partial<Pick<InventoryProduct, 'name' | 'category' | 'price' | 'min_threshold' | 'barcode'>>
    ) => Promise<{ success: boolean; error?: string }>;
    removeProduct: (id: number) => Promise<{ success: boolean; error?: string }>;
    addMovement: (input: {
        product_id: number;
        type: MovementType;
        quantity: number;
        unit_price?: number | null;
        client_id?: string | null;
        hairdresser_id?: string | null;
        note?: string | null;
    }) => Promise<{ success: boolean; error?: string }>;
    sell: (input: {
        client_id?: string | null;
        hairdresser_id?: string | null;
        note?: string | null;
        lines: SaleLine[];
    }) => Promise<{ success: boolean; error?: string }>;
}

const InventoryContext = createContext<InventoryContextValue | null>(null);

export const useInventory = () => {
    const context = useContext(InventoryContext);
    if (!context) {
        throw new Error('useInventory must be used within an InventoryProvider');
    }
    return context;
};

export const InventoryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { organization, isAuthenticated, user } = useAuth();
    const [products, setProducts] = useState<InventoryProduct[]>([]);
    const [movements, setMovements] = useState<InventoryMovement[]>([]);
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async () => {
        if (!organization?.id) return;
        setLoading(true);
        const [p, m] = await Promise.all([
            fetchProducts(organization.id),
            fetchMovements(organization.id),
        ]);
        if (p.success && p.data) setProducts(p.data);
        if (m.success && m.data) setMovements(m.data);
        setLoading(false);
    }, [organization?.id]);

    useEffect(() => {
        if (isAuthenticated && organization?.id) refresh();
    }, [isAuthenticated, organization?.id, refresh]);

    const findByBarcode = useCallback(
        (barcode: string) => products.find((p) => (p.barcode || '') === barcode.trim()),
        [products]
    );

    const getProductById = useCallback(
        (id: number) => products.find((p) => p.id === id),
        [products]
    );

    const hasMovements = useCallback(
        (productId: number) => movements.some((m) => m.product_id === productId),
        [movements]
    );

    const addProduct = useCallback(
        async (input: Omit<CreateProductInput, 'shop_id'>) => {
            if (!organization?.id) return { success: false, error: 'Nessun salone attivo' };
            const res = await createProductService({ ...input, shop_id: Number(organization.id) });
            if (res.success && res.data) setProducts((prev) => [...prev, res.data!].sort((a, b) => a.name.localeCompare(b.name)));
            return res;
        },
        [organization?.id]
    );

    const editProduct = useCallback(async (id: number, patch: any) => {
        const res = await updateProductService(id, patch);
        if (res.success && res.data) {
            setProducts((prev) => prev.map((p) => (p.id === id ? res.data! : p)));
        }
        return { success: res.success, error: res.error };
    }, []);

    const removeProduct = useCallback(
        async (id: number) => {
            // con movimenti registrati il database blocca la cancellazione: si archivia
            const res = hasMovements(id)
                ? await archiveProductService(id)
                : await deleteProductService(id);
            if (res.success) setProducts((prev) => prev.filter((p) => p.id !== id));
            return res;
        },
        [hasMovements]
    );

    const addMovement = useCallback(
        async (input: {
            product_id: number;
            type: MovementType;
            quantity: number;
            unit_price?: number | null;
            client_id?: string | null;
            hairdresser_id?: string | null;
            note?: string | null;
        }) => {
            if (!organization?.id) return { success: false, error: 'Nessun salone attivo' };
            const res = await registerMovementService({
                ...input,
                shop_id: Number(organization.id),
                created_by: user?.id ?? null,
            });
            if (res.success && res.data) {
                const mov = res.data;
                setMovements((prev) => [mov, ...prev]);
                setProducts((prev) =>
                    prev.map((p) =>
                        p.id === mov.product_id
                            ? {
                                ...p,
                                stock: p.stock + (mov.type === 'in' ? mov.quantity : -mov.quantity),
                            }
                            : p
                    )
                );
            }
            return { success: res.success, error: res.error };
        },
        [organization?.id, user?.id]
    );

    const sell = useCallback(
        async (input: {
            client_id?: string | null;
            hairdresser_id?: string | null;
            note?: string | null;
            lines: SaleLine[];
        }) => {
            if (!organization?.id) return { success: false, error: 'Nessun salone attivo' };
            const res = await registerSaleService({
                ...input,
                shop_id: Number(organization.id),
                created_by: user?.id ?? null,
            });
            if (res.success && res.data) {
                const newMovements = res.data.movements;
                setMovements((prev) => [...newMovements, ...prev]);
                setProducts((prev) =>
                    prev.map((p) => {
                        const sold = newMovements
                            .filter((m) => m.product_id === p.id)
                            .reduce((sum, m) => sum + m.quantity, 0);
                        return sold ? { ...p, stock: p.stock - sold } : p;
                    })
                );
            }
            return { success: res.success, error: res.error };
        },
        [organization?.id, user?.id]
    );

    return (
        <InventoryContext.Provider
            value={{
                products,
                movements,
                loading,
                refresh,
                findByBarcode,
                getProductById,
                hasMovements,
                addProduct,
                editProduct,
                removeProduct,
                addMovement,
                sell,
            }}
        >
            {children}
        </InventoryContext.Provider>
    );
};
